import { cartAdd } from '@theme/cart-add';
import { containsBlacklistedWord } from '@theme/kit-blacklist';
import { applyMaxLength, parseLetterLimit } from '@theme/kit-input-limit';

class KitCustomizationWidget extends HTMLElement {
  connectedCallback() {
    let shopifyDomain, storefrontAccessToken;
    const hostname = window.location.hostname;

    if (hostname.includes('store.fcbarcelona.com')) {
      shopifyDomain = "store.fcbarcelona.com";
      storefrontAccessToken = "5966bf3924b4582b983020a50690016a";
    } else {
      shopifyDomain = "stage-blm-fcb.myshopify.com";
      storefrontAccessToken = "696aa85c0600050be9a5aed61155a0d1";
    }

    this._shopifyDomain = shopifyDomain;
    this._storefrontAccessToken = storefrontAccessToken;
    this._countryCode = document.documentElement.getAttribute("country")?.toUpperCase() || "ES";
    this._languageCode = document.documentElement.getAttribute("lang")?.toUpperCase() || "ES";
    this._currency = document.documentElement.getAttribute("currency")?.toUpperCase() || "EUR";
    this._currencySymbol = document.documentElement.getAttribute("currency-symbol") || "€";

    this.section = this;

    this.customizationWrapper = this.querySelector('.kcw__customization-wrapper');
    const customPriceRaw = this.customizationWrapper?.dataset.customizationPrice || "1500";
    this.customizationPrice = parseFloat(customPriceRaw) / 100;
    this.customizationVariantId = this.customizationWrapper?.dataset.customizationVariantId || null;
    this.customizationIsReturnable = this.customizationWrapper?.dataset.customizationIsReturnable === "true";

    this.isRendering = false;
    this.lastRenderOptions = null;
    this.lastRenderColors = null;

    this.state = {
      selectedKit: null,
      selectedQuality: null,
      selectedVariant: null,
      selectedCompetition: null,

      customizationMode: 'player',
      playerName: '',
      playerNumber: '',
      playerReference: null,

      poolBlankProducts: [],
      poolPrePrintedProducts: [],
      poolCompetitionDefinitions: [],

      baseProduct: null,
      backupProduct: null,
      baseProductKitType: null,

      selectedSizeId: null,
      selectedSizeSource: null,

      fictitiousProduct: null,
      serigraphyGeometry: {},
      productColors: {},
      currentBadges: []
    };

    this.allPlayersCache = null;
    this.isUpdatingUI = false;
    this.fetchingKeys = new Set();
    this.isInitializing = true;
    this.fictitiousCache = new Map();
    this.candidatesCache = new Map();
    this.updateFictitiousVersion = 0;

    this.canvas = this.querySelector('#kit-customization-canvas');
    this.baseImage = this.querySelector('#kit-customization-image');
    this.sizeContainer = this.querySelector('.kcw__size-container');
    this.sizeGrid = this.querySelector('#kcw-size-selector-grid');
    this.compContainer = this.querySelector('.kcw__competition-options');

    this.elKitPrice = this.querySelector('#kcw-kit-header-price');
    this.elCompPrice = this.querySelector('#kcw-competition-header-price');
    this.elCustomPrice = this.querySelector('#kcw-customization-header-price');
    this.elSubtotal = this.querySelector('.kcw__subtotal-price');
    this.elKitTitleMobile = this.querySelector('#kcw-header-kit-title-mobile');
    this.elKitTitleDesktop = this.querySelector('#kcw-header-kit-title-desktop');

    this.renderer = null;
    this.sectionOffsetTop = null;

    this.setup();
    this.initDefaults();
    window.kitCustomizationWidget = this;
  }

  disconnectedCallback() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
    }
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
  }

  setup() {
    this.setupKitSelectors();
    this.setupQualitySelectors();
    this.setupVariantSelectors();
    this.setupCustomizationLogic();
    this.setupAddToCartButton();
    this.preloadAllFonts();

    this._resizeObserver = new ResizeObserver(() => { this.resizeCanvas(); });
    if (this.baseImage) this._resizeObserver.observe(this.baseImage);

    const nameInput = this.section.querySelector('#kcw-player-name');
    const numberInput = this.section.querySelector('#kcw-player-number');
    [nameInput, numberInput].forEach(input => {
      if (!input) return;
      input.addEventListener('blur', () => {
        if (window.innerWidth > 749) return;
        setTimeout(() => {
          this.section.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 350);
      });
    });

    this._intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && window.innerWidth <= 749) {
        this.handleMobileLayout();
        this._intersectionObserver.disconnect();
      }
    }, { threshold: 0.01 });
    this._intersectionObserver.observe(this);

    this._resizeHandler = () => {
      if (window.innerWidth > 749) {
        this.section.style.height = '';
        this.handleMobileLayout();
        this.resizeCanvas();
        return;
      }
      const activeEl = document.activeElement;
      const isInputFocused = activeEl && activeEl.matches('.kcw__input-text, .kcw__select');
      if (isInputFocused) return;
      if (this.section.style.height) return;
      this.handleMobileLayout();
      this.resizeCanvas();
    };
    window.addEventListener('resize', this._resizeHandler);

    requestAnimationFrame(() => { this.handleMobileLayout(); });
  }

  setupAddToCartButton() {
    const btn = this.querySelector('#kcw-add-to-cart-btn');
    if (!btn) return;

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      this.addToCartSubmission();
    });
  }

  initDefaults() {
    setTimeout(() => {
      const firstKitBtn = this.section.querySelector('.kcw__selector--kit.selected') ||
        this.section.querySelector('.kcw__selector--kit');

      if (firstKitBtn) {
        this.selectKit(firstKitBtn);
      }

      setTimeout(() => {
        this.isInitializing = false;
        if (window.innerWidth <= 749) {
          const optionsContainer = this.section.querySelector('.kcw__options');
          if (optionsContainer) {
            optionsContainer.scrollTop = 0;
          }
        }
      }, 500);
    }, 200);
  }

  selectKit(btn) {
    if (!btn) return;

    const allBtns = this.section.querySelectorAll('.kcw__selector--kit');
    allBtns.forEach(b => b.classList.remove('selected'));
    btn.classList.add('selected');

    try {
      this.state.poolBlankProducts = JSON.parse(btn.dataset.baseProducts || "[]");
      this.state.poolPrePrintedProducts = JSON.parse(btn.dataset.prePrintedProducts || "[]");
      this.state.poolCompetitionDefinitions = JSON.parse(btn.dataset.competitionDefinitions || "[]");
      this.state.productImages = JSON.parse(btn.dataset.productImages || "{}");
      const preloadedColors = JSON.parse(btn.dataset.kitColors || "{}");
      if (preloadedColors.fill) this.state.productColors = preloadedColors;
    } catch (e) { console.error("Data parse error", e); }

    this.state.selectedKit = {
      id: btn.dataset.kcwKitId,
      handle: btn.dataset.kcwKitHandle,
      title: btn.dataset.kitTitle,
      renderImage: btn.dataset.renderImage,
      renderSrcset: btn.dataset.renderSrcset,
      bgColor: btn.dataset.bgColor,
      sportId: btn.dataset.sportMetaId,
      roleId: btn.dataset.roleMetaId,
      seasonId: btn.dataset.seasonMetaId,
      sportHandle: btn.dataset.sportHandle,
      roleHandle: btn.dataset.roleHandle,
      seasonHandle: btn.dataset.seasonHandle,
      seasonLabel: btn.dataset.seasonLabel
    };

    this.updateKitTitle();
    this.updateKitImage();
    this.preloadGenderImages();
    this.preloadCompetitionBadges();
    this.lastRenderColors = null;
    this.lastRenderOptions = null;
    this.renderer = null;

    this.isUpdatingUI = true;

    const qualities = JSON.parse(btn.dataset.qualityOptions || "[]");
    const variants = JSON.parse(btn.dataset.variantOptions || "[]");
    this.renderOptions(qualities, '.kcw__selector-group--quality', 'quality');
    this.renderOptions(variants, '.kcw__selector-group--variant', 'variant');

    if (qualities.length > 0) {
      const q = qualities[0];
      this.state.selectedQuality = { id: q.system.id, handle: q.system.handle, label: q.label };
    }
    if (variants.length > 0) {
      const v = variants[0];
      this.state.selectedVariant = {
        ageGroupId: v.age_group.value.system.id,
        targetGenderId: v.target_gender.value.system.id,
        ageGroupHandle: v.age_group.value.system.handle,
        targetGenderHandle: v.target_gender.value.system.handle,
        label: v.label
      };
    }

    this.isUpdatingUI = false;

    this.validateVariantAvailability();
    this.refreshProductContext();
    this.scrollToNextOption('kit');
  }

  refreshProductContext() {
    if (!this.state.selectedKit || !this.state.selectedQuality || !this.state.selectedVariant) return;

    const criteria = {
      quality: this.state.selectedQuality.handle,
      age: this.state.selectedVariant.ageGroupHandle,
      gender: this.state.selectedVariant.targetGenderHandle
    };

    const matchingBlanks = this.filterProductsByAttributes(this.state.poolBlankProducts, criteria);
    const matchingPrePrinted = this.filterProductsByAttributes(this.state.poolPrePrintedProducts, criteria);

    const availableCompHandles = new Set();
    [...matchingBlanks, ...matchingPrePrinted].forEach(prod => {
      if (prod.metafields.competitions) {
        prod.metafields.competitions.forEach(c => availableCompHandles.add(c));
      }
    });

    this.renderCompetitionSelector(availableCompHandles);
    this.determineTrueBaseProduct(matchingBlanks, matchingPrePrinted);
  }

  validateVariantAvailability() {
    if (!this.state.selectedQuality) return;

    const qualityHandle = this.state.selectedQuality.handle;
    const inputs = this.section.querySelectorAll('.kcw__selector-radio--variant');
    let selectedInputIsDisabled = false;
    let firstAvailableInput = null;

    inputs.forEach(input => {
      const age = input.dataset.ageGroupHandle;
      const gender = input.dataset.targetGenderHandle;
      const criteria = { quality: qualityHandle, age, gender };

      const hasProducts =
        this.filterProductsByAttributes(this.state.poolBlankProducts, criteria).length > 0 ||
        this.filterProductsByAttributes(this.state.poolPrePrintedProducts, criteria).length > 0;

      const label = input.closest('.kcw__selector-option--variant');

      if (hasProducts) {
        input.disabled = false;
        if (label) label.classList.remove('disabled');
        if (!firstAvailableInput) firstAvailableInput = input;
      } else {
        input.disabled = true;
        if (label) label.classList.add('disabled');
        if (input.checked) selectedInputIsDisabled = true;
      }
    });

    if (selectedInputIsDisabled && firstAvailableInput) {
      firstAvailableInput.checked = true;
      firstAvailableInput.dispatchEvent(new Event('change'));
    }
  }

  filterProductsByAttributes(pool, criteria) {
    return pool.filter(p => {
      const pQuality = String(p.metafields.quality || "").trim();
      const pAge = String(p.metafields.age_group || "").trim();
      const pGender = String(p.metafields.target_gender || "").trim();
      return pQuality === criteria.quality && pAge === criteria.age && pGender === criteria.gender;
    });
  }

  determineTrueBaseProduct(blanksOverride = null, preOverride = null) {
    let blanks = blanksOverride;
    let prePrinted = preOverride;

    if (!blanks) {
      const criteria = {
        quality: this.state.selectedQuality.handle,
        age: this.state.selectedVariant.ageGroupHandle,
        gender: this.state.selectedVariant.targetGenderHandle
      };
      blanks = this.filterProductsByAttributes(this.state.poolBlankProducts, criteria);
      prePrinted = this.filterProductsByAttributes(this.state.poolPrePrintedProducts, criteria);
    }

    if (!this.state.selectedCompetition) {
      this.handleNoBaseProduct();
      return;
    }

    const currentCompHandle = this.state.selectedCompetition.handle;

    let match = prePrinted.find(p => p.metafields.competitions.includes(currentCompHandle));
    let backup = null;

    if (match) {
      backup = blanks.find(p => p.metafields.competitions.includes(currentCompHandle)) || blanks[0];
    } else {
      match = blanks.find(p => p.metafields.competitions.includes(currentCompHandle));
    }

    if (match) {
      this.state.baseProduct = match;
      this.state.baseProductIsReturnable = match.is_returnable || false;
      this.state.backupProduct = backup;
      if (backup) {
        this.state.backupProductIsReturnable = backup.is_returnable || false;
      }
      this.state.baseProductKitType = match.type === 'blank' ? 'base' : 'pre-printed-competition-base';

      this.renderSizeSelectorWithFallback();
      this.updateAllPrices();
      this.updateAddToCartButton();

      this.filterPlayerOptions();

      if (this.state.playerReference && !this.validatePlayerCompatibility()) {
        this.resetCustomizationState();
        const playerSelect = this.querySelector('#kcw-player-select');
        if (playerSelect) playerSelect.value = "";
        this.state.fictitiousProduct = null;
        this.updateAllPrices();
        this.updateAddToCartButton();
      }

      if (this.hasActiveCustomization()) {
        this.drawCustomizations();
      }

      this.fetchFictitiousProduct();

    } else {
      this.handleNoBaseProduct();
      this.filterPlayerOptions();
    }
  }

  handleNoBaseProduct() {
    this.state.baseProduct = null;
    this.state.backupProduct = null;
    if (this.sizeGrid) this.sizeGrid.innerHTML = '<div class="kcw__loading-sizes">Unavailable combination.</div>';
    if (this.elSubtotal) this.elSubtotal.textContent = "---";
  }

  filterPlayerOptions() {
    const select = this.querySelector('#kcw-player-select');
    if (!select) return;

    if (!this.allPlayersCache) {
      this.allPlayersCache = [];
      // Index 0 is the placeholder option — start from 1
      for (let i = 1; i < select.options.length; i++) {
        const opt = select.options[i];
        this.allPlayersCache.push({
          value: opt.value,
          text: opt.textContent,
          dataset: { ...opt.dataset }
        });
      }
    }

    if (!this.state.selectedKit || !this.state.selectedCompetition) {
      select.length = 1;
      select.disabled = true;
      return;
    }

    const safe = (str) => String(str || "").toLowerCase().trim();
    const normalizeGender = (g) => {
      let val = safe(g);
      if (val === 'mens' || val === 'man') return 'male';
      if (val === 'womens' || val === 'woman') return 'female';
      return val;
    };

    const targetGender = normalizeGender(this.state.selectedCompetition.gender);
    const targetSeason = safe(this.state.selectedKit.seasonHandle);
    const targetSport = safe(this.state.selectedKit.sportHandle);
    const targetRole = safe(this.state.selectedKit.roleHandle);


    const previousValue = select.value;
    select.length = 1;
    let matchesFound = 0;

    this.allPlayersCache.forEach(p => {
      const pGender = normalizeGender(p.dataset.gender);
      const pSeason = safe(p.dataset.seasonHandle);
      const pSport = safe(p.dataset.sportHandle);
      const pRole = safe(p.dataset.roleHandle);

      const isMatch = (pGender === targetGender) &&
        (pSeason === targetSeason) &&
        (pSport === targetSport) &&
        (pRole === targetRole);

      if (isMatch) {
        const opt = document.createElement('option');
        opt.value = p.value;
        opt.textContent = p.text;
        opt.dataset.name = p.dataset.name;
        opt.dataset.number = p.dataset.number;
        select.appendChild(opt);
        matchesFound++;
      }
    });


    if (matchesFound > 0) {
      select.disabled = false;
      const exists = Array.from(select.options).some(o => o.value === previousValue);
      if (exists && previousValue) {
        select.value = previousValue;
      } else if (previousValue) {
        this.resetCustomizationState();
        this.drawCustomizations();
        this.updateAllPrices();
      }
    } else {
      const opt = document.createElement('option');
      opt.textContent = "No players available";
      select.appendChild(opt);
      select.disabled = true;
      this.resetCustomizationState();
    }
  }

  renderCompetitionSelector(availableHandles) {
    if (!this.compContainer) return;
    this.compContainer.innerHTML = "";

    const validDefinitions = this.state.poolCompetitionDefinitions.filter(def =>
      availableHandles.has(def.system.handle)
    );

    if (validDefinitions.length === 0) {
      this.compContainer.innerHTML = "<span style='padding:10px; font-size:12px;'>No competitions available.</span>";
      this.state.selectedCompetition = null;
      return;
    }

    validDefinitions.forEach((def, index) => {
      const isSelected = (index === 0);
      const seriJSON = JSON.stringify(def.serigraphy || {}).replace(/"/g, '&quot;');
      const gender = def.gender?.value?.label || "";

      const html = `
<label class="kcw__selector-option kcw__competition-label ${isSelected ? 'selected' : ''}" data-competition-handle="${def.system.handle}">
<input type="radio" name="kcw_competition" value="${def.label}" class="kcw__competition-radio" ${isSelected ? 'checked' : ''}
data-competition-id="${def.system.id}"
data-badge-price="${def.badge_price}"
data-badge-variant-id="${def.badge_variant_id}"
data-badge-is-returnable="${def.badge_is_returnable}"
data-laliga="${def.laliga === true || def.laliga === 'true'}"
data-no-badge="${def.no_badge === true}"
data-serigraphy="${seriJSON}"
data-serigraphy-handle="${def.serigraphy_handle || ''}"
data-gender="${gender}">
<span class="kcw__competition-badge">
${def.icon_svg}
<span class="kcw__competition-name">${def.title || def.label}</span>
</span>
</label>`;
      this.compContainer.insertAdjacentHTML("beforeend", html);
    });

    this.setupCompetitionSelectors();

    if (validDefinitions.length > 0) {
      const def = validDefinitions[0];
      const gender = def.gender?.value?.label || "";


      this.state.selectedCompetition = {
        label: def.label,
        title: def.title,
        handle: def.system.handle,
        id: def.system.id,
        badgePrice: parseFloat(def.badge_price || 0),
        badgeVariantId: def.badge_variant_id,
        badgeIsReturnable: def.badge_is_returnable || false,
        laliga: def.laliga === true || def.laliga === 'true',
        noBadge: def.no_badge === true,
        serigraphyHandle: def.serigraphy_handle || null,
        gender: gender.toLowerCase()
      };
      try {
        this.state.serigraphyGeometry = def.serigraphy || {};
      } catch (e) {
        this.state.serigraphyGeometry = {};
      }
    }
  }

    #applyBaseImageSources({ src, srcset }) {
    if (!this.baseImage || !src) return;

    // Evita el parpadeo: precargamos la nueva imagen fuera de pantalla y solo
    // intercambiamos el src/srcset visible cuando ya está decodificada, de modo
    // que la imagen anterior permanece a la vista hasta que la nueva esté lista.
    const applySwap = () => {
      this.baseImage.src = src;
      if (srcset) {
        this.baseImage.srcset = srcset;
      } else {
        this.baseImage.removeAttribute('srcset');
      }
    };

    // Token para descartar cargas obsoletas si el usuario cambia de opción rápido.
    this._baseImageLoadToken = (this._baseImageLoadToken || 0) + 1;
    const token = this._baseImageLoadToken;

    const preloader = new Image();
    if (srcset) preloader.srcset = srcset;
    preloader.src = src;

    const finalize = () => {
      if (token !== this._baseImageLoadToken) return; // otra carga más reciente ganó
      applySwap();
    };

    const decoded = typeof preloader.decode === 'function' ? preloader.decode() : null;
    if (decoded) {
      decoded.then(finalize).catch(finalize);
    } else if (preloader.complete) {
      finalize();
    } else {
      preloader.onload = finalize;
      preloader.onerror = finalize;
    }
  }


  updateKitTitle() {
    const title = this.state.selectedKit?.title;
    if (title == null) return;
    if (this.elKitTitleMobile) this.elKitTitleMobile.textContent = title;
    if (this.elKitTitleDesktop) this.elKitTitleDesktop.textContent = title;
  }

  updateKitImage() {
    if (this.state.selectedKit && this.baseImage) {
      this.#applyBaseImageSources({
        src: this.state.selectedKit.renderImage,
        srcset: this.state.selectedKit.renderSrcset,
      });
      const gradientLayer = this.section.querySelector('.kcw__gradient-layer');
      if (gradientLayer && this.state.selectedKit.bgColor) {
        gradientLayer.style.setProperty('--kcw-bg-color', this.state.selectedKit.bgColor);
      }
    }
  }

  preloadGenderImages() {
    if (!this.state.productImages) return;

    const genders = ['men', 'women', 'junior'];
    const currentQuality = this.state.selectedQuality?.handle;
    const currentCompetition = this.state.selectedCompetition?.handle;

    const toPreload = [];
    Object.entries(this.state.productImages).forEach(([handle, imgData]) => {
      if (genders.includes(imgData.gender)) {
        if (currentQuality && imgData.quality !== currentQuality) return;
        if (currentCompetition && !imgData.competitions.includes(currentCompetition)) return;
        toPreload.push(imgData.imageUrl);
      }
    });

    toPreload.forEach(url => {
      const img = new Image();
      img.src = url;
    });
  }

  preloadCompetitionBadges() {
    if (!this.state.poolCompetitionDefinitions || this.state.poolCompetitionDefinitions.length === 0) return;

    const toPreload = [];
    this.state.poolCompetitionDefinitions.forEach(comp => {
      if (comp.icon_svg) {
        toPreload.push({ type: 'svg', data: comp.icon_svg });
      }
      if (comp.logo) {
        toPreload.push({ type: 'image', url: comp.logo });
      }
    });

    toPreload.forEach(item => {
      if (item.type === 'svg') {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        const blob = new Blob([item.data], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        img.onload = () => URL.revokeObjectURL(url);
        img.src = url;
        if (!window.badgeCache) window.badgeCache = {};
        window.badgeCache[item.data] = img;
      } else if (item.type === 'image') {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = item.url;
        if (!window.badgeCache) window.badgeCache = {};
        window.badgeCache[item.url] = img;
      }
    });
  }

  preloadProductBadges(badges) {
    if (!badges || badges.length === 0) return Promise.resolve();

    const promises = badges.map(badge => {
      return new Promise((resolve) => {
        const key = badge.renderPng || badge.renderSvg;
        if (!key) {
          resolve();
          return;
        }

        if (window.badgeCache && window.badgeCache[key]) {
          resolve();
          return;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          if (!window.badgeCache) window.badgeCache = {};
          window.badgeCache[key] = img;
          resolve();
        };

        img.onerror = () => resolve();

        if (badge.renderSvg) {
          const fillColor = this.canvas?.dataset?.sponsorsColor || '#ffffff';
          let svgString = decodeURIComponent(badge.renderSvg).replace(/fill="[^"]*"/gi, `fill="${fillColor}"`);
          const blob = new Blob([svgString], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          img.src = url;
        } else if (badge.renderPng) {
          img.src = badge.renderPng;
        } else {
          resolve();
        }
      });
    });

    return Promise.all(promises);
  }

  updateProductImage(productHandle) {
    if (!productHandle || !this.state.productImages || !this.baseImage) return;

    const imageData = this.state.productImages[productHandle];
    if (!imageData || !imageData.imageUrl) return;

    if (this.baseImage.src === imageData.imageUrl) return;

    this.#applyBaseImageSources({
      src: imageData.imageUrl,
      srcset: imageData.imageSrcSet,
    });
  }

  renderSizeSelectorWithFallback() {
    if (!this.sizeGrid || !this.state.baseProduct) return;
    this.sizeGrid.innerHTML = "";
    this.state.selectedSizeId = null;

    if (this.sizeContainer) this.sizeContainer.style.display = 'block';

    const baseVariants = this.state.baseProduct.variants;
    const backupVariants = this.state.backupProduct ? this.state.backupProduct.variants : [];

    baseVariants.forEach((variant) => {
      let isAvailable = variant.available;
      let source = 'base';
      let finalVariantId = variant.id;

      if (!isAvailable && this.state.backupProduct) {
        const backupVar = backupVariants.find(v => v.title === variant.title);
        if (backupVar && backupVar.available) {
          isAvailable = true;
          source = 'backup';
          finalVariantId = backupVar.id;
        }
      }

      const div = document.createElement('label');
      div.className = `kcw__size-option ${!isAvailable ? 'disabled' : ''}`;
      div.innerHTML = `
<input type="radio" name="kcw-size" value="${finalVariantId}"
  data-source="${source}"
  class="kcw__size-input" ${!isAvailable ? 'disabled' : ''}>
<span>${variant.title}</span>`;

      div.querySelector('input').addEventListener('change', (e) => {
        this.state.selectedSizeId = e.target.value;
        this.state.selectedSizeSource = e.target.dataset.source;
        this.sizeGrid.querySelectorAll('.kcw__size-option').forEach(o => o.classList.remove('selected'));
        div.classList.add('selected');
        this.sizeContainer.classList.remove('has-error');
        this.syncNameLetterLimit();
        this.updateAddToCartButton();

        if (!this.isInitializing && window.innerWidth <= 749) {
          const optionsContainer = this.section.querySelector('.kcw__options');
          const customizationWrapper = this.section.querySelector('.kcw__customization-wrapper');
          const competitionContainer = this.section.querySelector('.kcw__competition-container');
          const targetElement = customizationWrapper || competitionContainer;

          if (optionsContainer && targetElement) {
            setTimeout(() => {
              const containerRect = optionsContainer.getBoundingClientRect();
              const elementRect = targetElement.getBoundingClientRect();
              const scrollOffset = elementRect.top - containerRect.top + optionsContainer.scrollTop;

              optionsContainer.scrollTo({
                top: scrollOffset,
                behavior: 'smooth'
              });
            }, 150);
          }
        }
      });

      this.sizeGrid.appendChild(div);
    });

    // Sizes were reloaded and none is selected yet: use the first available variant's limit
    this.syncNameLetterLimit();
  }

  /**
   * Finds a variant by id in the current base product or its backup.
   * @param {string|null} variantId
   */
  findLoadedVariant(variantId) {
    if (!variantId) return null;
    const variants = [
      ...(this.state.baseProduct?.variants || []),
      ...(this.state.backupProduct?.variants || []),
    ];
    return variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  /**
   * Updates #kcw-player-name maxlength from the letter_limit variant metafield:
   * - selected size → that variant's limit
   * - no size selected (sizes just reloaded) → first available variant in the size grid
   * Falls back to the first available limit, then to the input's data-fallback-letter-limit.
   * The current value is only touched if it exceeds the new limit (extra letters removed).
   */
  syncNameLetterLimit() {
    const nameInput = /** @type {HTMLInputElement|null} */ (this.querySelector('#kcw-player-name'));
    if (!nameInput || !this.state.baseProduct) return;

    const firstAvailableInput = /** @type {HTMLInputElement|null} */ (
      this.sizeGrid?.querySelector('.kcw__size-input:not(:disabled)')
    );
    const firstAvailableVariant =
      this.findLoadedVariant(firstAvailableInput?.value ?? null) ||
      this.state.baseProduct.variants?.find((variant) => variant.available) ||
      this.state.baseProduct.variants?.[0] ||
      null;
    const selectedVariant = this.findLoadedVariant(this.state.selectedSizeId);

    const limit =
      parseLetterLimit(selectedVariant?.letter_limit) ??
      parseLetterLimit(firstAvailableVariant?.letter_limit) ??
      parseLetterLimit(nameInput.dataset.fallbackLetterLimit) ??
      12;

    applyMaxLength(nameInput, limit);
  }

  calculateTargetHandle() {
    const baseKitType = this.state.baseProduct?.metafields?.kit_type;

    if (!baseKitType) {
      return null;
    }

    let prefix = "";

    if (baseKitType === "base") {
      prefix = "competition";
    } else if (baseKitType === "pre-printed-competition-base") {
      prefix = "pre-printed-competition";
    } else if (baseKitType === "no-competition-base") {
      prefix = "no-competition";
    } else {
      prefix = "competition";
    }

    let suffix = "fictitious";

    if (this.state.customizationMode === 'player' && this.state.playerReference) {
      suffix = "player-fictitious";
    }
    else if (this.state.customizationMode === 'custom' && this.hasActiveCustomization()) {
      suffix = "custom-fictitious";
    }

    return `${prefix}-${suffix}`;
  }

  generateCacheKey() {
    const parts = [
      this.state.selectedKit?.id || '',
      this.state.selectedKit?.seasonId || '',
      this.state.selectedQuality?.id || '',
      this.state.selectedKit?.roleId || '',
      this.state.selectedCompetition?.id || '',
      this.state.selectedVariant?.targetGenderHandle || '',
      this.state.playerReference || '',
      this.hasActiveCustomization() ? 'custom' : 'no-custom'
    ];
    return parts.join('|');
  }

  validatePlayerCompatibility() {
    if (!this.state.playerReference) return true;
    if (!this.allPlayersCache) return true;

    const playerData = this.allPlayersCache.find(p => p.value === this.state.playerReference);
    if (!playerData) return false;

    const safe = (str) => String(str || "").toLowerCase().trim();
    const normalizeGender = (g) => {
      let val = safe(g);
      if (val === 'mens' || val === 'man') return 'male';
      if (val === 'womens' || val === 'woman') return 'female';
      return val;
    };

    const playerGender = normalizeGender(playerData.dataset.gender);
    const competitionGender = safe(this.state.selectedCompetition?.gender || "");
    const variantGender = safe(this.state.selectedVariant?.targetGenderHandle || "");

    const contextGender = competitionGender || normalizeGender(variantGender);

    return playerGender === contextGender;
  }

  async fetchFictitiousProduct() {
    if (!this.state.baseProduct) return;

    const targetHandle = this.calculateTargetHandle();
    const kitTypeId = window.kcwKitTypes ? window.kcwKitTypes[targetHandle] : null;

    if (!targetHandle || !kitTypeId) {
      this.updateFictitiousData(null);
      return;
    }

    const cacheKey = this.generateCacheKey();

    if (this.fictitiousCache.has(cacheKey)) {
      const cached = this.fictitiousCache.get(cacheKey);
      await this.updateFictitiousData(cached);
      return;
    }

    if (this.fetchingKeys.has(cacheKey)) return;
    this.fetchingKeys.add(cacheKey);

    const requestCacheKey = cacheKey;
    const snapshot = {
      compHandle: this.state.selectedCompetition?.handle || null,
      baseProductId: this.state.baseProduct?.id || null,
      kitId: this.state.selectedKit?.id,
      kitSeasonId: this.state.selectedKit?.seasonId,
      qualityId: this.state.selectedQuality?.id,
      kitRoleId: this.state.selectedKit?.roleId,
      playerReference: this.state.playerReference || null,
    };

    const queryCacheKey = [snapshot.kitId, snapshot.kitSeasonId, snapshot.qualityId, snapshot.kitRoleId, targetHandle, snapshot.playerReference || ''].join('|');

    const countryCode = this._countryCode;
    const languageCode = this._languageCode;

    try {

      const toGid = (id, type) => id ? `gid://shopify/${type}/${String(id).split('/').pop()}` : null;

      let candidates;

      if (this.candidatesCache.has(queryCacheKey)) {
        candidates = this.candidatesCache.get(queryCacheKey);
      } else {
        const filters = [];
        filters.push(`{ productMetafield: { namespace: "customization", key: "kit_type", value: "${toGid(kitTypeId, 'Metaobject')}" } }`);
        filters.push(`{ productMetafield: { namespace: "customization", key: "kit", value: "${toGid(snapshot.kitId, 'Metaobject')}" } }`);
        filters.push(`{ productMetafield: { namespace: "customization", key: "season", value: "${toGid(snapshot.kitSeasonId, 'Metaobject')}" } }`);
        filters.push(`{ productMetafield: { namespace: "customization", key: "quality", value: "${toGid(snapshot.qualityId, 'Metaobject')}" } }`);
        filters.push(`{ productMetafield: { namespace: "customization", key: "role", value: "${toGid(snapshot.kitRoleId, 'Metaobject')}" } }`);
        if (snapshot.playerReference) {
          filters.push(`{ productMetafield: { namespace: "customization", key: "back", value: "${toGid(snapshot.playerReference, 'Metaobject')}" } }`);
        }


        const query = `
query FindFictitiousMinimal($countryCode: CountryCode!, $languageCode: LanguageCode!) @inContext(country: $countryCode, language: $languageCode) {
collection(handle: "all-products") {
products(first: 250, filters: [${filters.join(',')}]) {
edges {
node {
id, title, handle
base_product: metafield(namespace: "customization", key: "base_product") {
  value
  reference { ... on Product { handle } }
}
competition: metafield(namespace: "customization", key: "competition") { references(first: 10) { edges { node { ... on Metaobject { handle } } } } }
back: metafield(namespace: "customization", key: "back") { value }
priceRange { minVariantPrice { amount currencyCode } }
badges: metafield(namespace: "custom", key: "badges") { key, value, references(first:10){edges{node{... on Metaobject{fields{key,value,reference{... on MediaImage{image{url}}}}}}}}}
}
}
}
}
}
`;

        const res = await this.shopifyQuery(query, { countryCode, languageCode });
        candidates = res?.data?.collection?.products?.edges?.map(e => e.node) || [];
        this.candidatesCache.set(queryCacheKey, candidates);
      }

      const matchedProduct = this.findExactMatch(candidates, targetHandle, snapshot.compHandle, snapshot.baseProductId);

      if (matchedProduct) {
        this.fictitiousCache.set(requestCacheKey, matchedProduct);
      }

      if (requestCacheKey === this.generateCacheKey()) {
        await this.updateFictitiousData(matchedProduct);
      }

    } catch (err) {
      console.error("API Error", err);
      if (requestCacheKey === this.generateCacheKey()) {
        this.updateFictitiousData(null);
      }
    } finally {
      this.fetchingKeys.delete(requestCacheKey);
    }
  }

  findExactMatch(candidates, targetHandle, requestCompHandle, requestBaseProductId) {
    if (candidates.length === 0) return null;

    const cleanId = (val) => val ? String(val).split('/').pop() : "---";
    const safe = (str) => str ? String(str).toLowerCase().trim() : "---";

    const getRefList = (f) => f?.references?.edges?.map(e => safe(e.node?.handle)) || [];

    const targetBaseId = cleanId(requestBaseProductId);
    const targetComp = requestCompHandle ? safe(requestCompHandle) : "---";

    const report = candidates.map(p => {
      const candidateBaseId = cleanId(p.base_product?.value);
      const candidateCompList = getRefList(p.competition);

      let isMatch = (candidateBaseId === targetBaseId);

      if (isMatch && !targetHandle.startsWith("no-competition")) {
        isMatch = candidateCompList.includes(targetComp);
      }

      return {
        "Product": p.handle.substring(0, 20) + "...",
        "BASE": `${candidateBaseId} [${candidateBaseId === targetBaseId ? 'OK' : 'FAIL'}]`,
        "COMP": `${candidateCompList} [${candidateCompList.includes(targetComp) ? 'OK' : 'FAIL'}]`,
        "RESULT": isMatch ? "WINNER" : "REJECT"
      };
    });

    const winner = candidates.find((_, i) => report[i].RESULT === "WINNER");
    return winner || null;
  }

  resizeCanvas() {
    if (!this.canvas || !this.baseImage) return;

    if (this.renderer && typeof this.renderer.setupCanvas === 'function') {
      this.renderer.setupCanvas();
    } else {
      const rect = this.baseImage.getBoundingClientRect();
      const scale = window.devicePixelRatio || 1;
      this.canvas.width = rect.width * scale;
      this.canvas.height = rect.height * scale;
      this.canvas.style.width = `${rect.width}px`;
      this.canvas.style.height = `${rect.height}px`;
    }
  }

  optionsChanged(newOptions, newColors) {
    if (!this.lastRenderOptions || !this.lastRenderColors) return true;

    const optionKeys = ['font', 'straightNameYPositionRatio', 'numberYPositionRatio', 'nameFontSizeRatio',
      'numberFontSizeRatio', 'arc', 'numberDots', 'numberDieCut', 'nameStrokeWidthRatio', 'numberStrokeWidthRatio', 'nameKerning',
      'nameStraightKerning', 'numberKerning', 'nameStrokeColor', 'numberStrokeColor',
      'numberRotationY', 'numberPerspectiveDistanceRatio', 'numberRotationZ'];
    const optionsChanged = optionKeys.some(key => this.lastRenderOptions[key] !== newOptions[key]);

    const colorKeys = ['fill', 'stroke', 'fill_number', 'stroke_number', 'fill_sponsors', 'dots_color'];
    const colorsChanged = colorKeys.some(key => this.lastRenderColors[key] !== newColors[key]);

    return optionsChanged || colorsChanged;
  }

  drawCustomizations() {
    if (!this.baseImage || !this.canvas) return;

    const sGeo = this.state.serigraphyGeometry || {};
    const sCol = this.state.productColors || {};

    this.canvas.dataset.nameColor = sCol.fill || '#FFFFFF';
    this.canvas.dataset.nameStrokeColor = sCol.stroke || 'transparent';
    this.canvas.dataset.numberColor = sCol.fill_number || sCol.fill || '#FFFFFF';
    this.canvas.dataset.numberStrokeColor = sCol.stroke_number || 'transparent';
    this.canvas.dataset.sponsorsColor = sCol.fill_sponsors || '#FFFFFF';
    // Blank -> the renderer falls back to the original black. Either way the
    // dots are painted at 50% opacity.
    this.canvas.dataset.dotsColor = sCol.dots_color || '';

    const fontName = sGeo.font || 'FCB-Play';

    const options = {
      font: fontName, textFont: fontName,
      straightNameYPositionRatio: sGeo.name_y_ratio ?? 0.381,
      numberYPositionRatio: sGeo.number_y_ratio ?? 0.759,
      nameFontSizeRatio: sGeo.name_size_ratio ?? 0.0744,
      numberFontSizeRatio: sGeo.number_size_ratio ?? 0.435,
      arc: sGeo.curved ?? false,
      numberDots: (typeof sGeo.number_dots === 'boolean') ? sGeo.number_dots : false,
      numberDieCut: (typeof sGeo.number_die_cut === 'boolean') ? sGeo.number_die_cut : false,
      nameStrokeWidthRatio: sGeo.name_stroke_width_ratio ?? 0,
      numberStrokeWidthRatio: sGeo.number_stroke_width_ratio ?? 0,
      nameKerning: sGeo.name_kerning ?? 0.0038,
      nameStraightKerning: sGeo.name_straight_kerning ?? null,
      numberKerning: sGeo.number_kerning ?? null,
      numberRotationY: sGeo.number_rotation_y ?? 0,
      numberPerspectiveDistanceRatio: sGeo.number_perspective_distance_ratio ?? 0.5,
      numberRotationZ: sGeo.number_rotation_z ?? 0,
      removeBackground: false
    };

    // ── LaLiga competitions: dedicated name/number stroke color ──
    // Keep this behavior in sync with kit.js (PDP). When the selected
    // competition has its `laliga` flag set, the name/number strokes use the
    // customization_color's `stroke_name_laliga` / `stroke_number_laliga`
    // fields, falling back to the regular `stroke` / `stroke_number`.
    if (this.state.selectedCompetition?.laliga) {
      const firstNonEmpty = (...values) => {
        for (const value of values) {
          const trimmed = typeof value === 'string' ? value.trim() : value;
          if (trimmed) return trimmed;
        }
        return undefined;
      };
      options.nameStrokeColor = firstNonEmpty(sCol.stroke_name_laliga, sCol.stroke);
      options.numberStrokeColor = firstNonEmpty(sCol.stroke_number_laliga, sCol.stroke_number);
    }


    const elements = { imgId: 'kit-customization-image', canvasId: 'kit-customization-canvas' };
    const badges = this.state.currentBadges || [];

    const needsNewRenderer = this.optionsChanged(options, sCol) || !this.renderer;

    if (needsNewRenderer) {
      if (this.isRendering) return;
      this.isRendering = true;

      const startRendering = () => {
        const currentBadges = this.state.currentBadges || [];
        this.renderer = new KitCanvasRenderer(this.state.playerName, this.state.playerNumber, elements, options, currentBadges);
        const self = this.renderer;
        this.renderer.drawBackground = function () {
          self.ctx.resetTransform();
          self.ctx.clearRect(0, 0, self.canvas.width, self.canvas.height);
          const dpr = window.devicePixelRatio || 1;
          const zoom = window.visualViewport?.scale || 1;
          self.ctx.scale(dpr / zoom, dpr / zoom);
          self.draw();
        };
        this.renderer.setupCanvas();
        this.lastRenderOptions = { ...options };
        this.lastRenderColors = { ...sCol };
        this.isRendering = false;

        if (this.pendingBadgeUpdate) {
          this.pendingBadgeUpdate = false;
          this.renderer.badges = this.state.currentBadges;
          this.renderer.pendingBadges = this.state.currentBadges.length;
          this.renderer.drawBackground();
        }
      };

      document.fonts.load(`bold 100px "${fontName}"`).then(() => {
        setTimeout(() => startRendering(), 50);
      }).catch(() => startRendering());
    } else {
      this.renderer.name = this.state.playerName;
      this.renderer.number = this.state.playerNumber;
      this.renderer.badges = this.state.currentBadges;
      this.renderer.pendingBadges = this.state.currentBadges.length;
      this.renderer.drawBackground();
    }
  }

  async updateFictitiousData(fictitiousProduct) {
    this.updateFictitiousVersion++;
    const myVersion = this.updateFictitiousVersion;

    this.state.fictitiousProduct = fictitiousProduct;
    this.updateAllPrices();
    this.updateAddToCartButton();

    if (fictitiousProduct?.base_product?.reference?.handle) {
      this.updateProductImage(fictitiousProduct.base_product.reference.handle);
    }

    const oldBadgesJson = JSON.stringify(this.state.currentBadges);

    const newBadges = [];
    if (fictitiousProduct && fictitiousProduct.badges && fictitiousProduct.badges.references) {
      fictitiousProduct.badges.references.edges.forEach(({ node }) => {
        const badgeData = {};
        if (node.fields) {
          node.fields.forEach(f => {
            const key = f.key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
            if (f.reference && f.reference.image) badgeData[key] = f.reference.image.url;
            else badgeData[key] = f.value;
          });
          newBadges.push(badgeData);
        }
      });
      await this.preloadProductBadges(newBadges);
    }

    if (myVersion !== this.updateFictitiousVersion) return;

    this.state.currentBadges = newBadges;

    const newBadgesJson = JSON.stringify(this.state.currentBadges);
    const badgesChanged = oldBadgesJson !== newBadgesJson;

    if (this.renderer) {
      this.renderer.badges = this.state.currentBadges;
      this.renderer.pendingBadges = this.state.currentBadges.length;
      if (typeof this.renderer.drawBackground === 'function') this.renderer.drawBackground();
      else this.renderer.draw();
    } else if (badgesChanged) {
      this.pendingBadgeUpdate = true;
      this.drawCustomizations();
    }
  }

  setupKitSelectors() {
    const kitButtons = this.section.querySelectorAll('.kcw__selector--kit');
    kitButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.selectKit(btn);
      });
    });
  }

  setupCompetitionSelectors() {
    const compInputs = this.section.querySelectorAll('.kcw__competition-radio');
    compInputs.forEach(input => {
      input.addEventListener('change', () => {
        const labels = this.section.querySelectorAll('.kcw__competition-label');
        labels.forEach(l => l.classList.remove('selected'));
        input.closest('.kcw__competition-label').classList.add('selected');

        this.updateSelectedCompetitionState(input);
        this.determineTrueBaseProduct();
        this.preloadGenderImages();
      });
    });
  }

  updateSelectedCompetitionState(input) {
    if (!input) return;
    const compGender = input.dataset.gender || "";
    this.state.selectedCompetition = {
      label: input.value,
      title: input.nextElementSibling.querySelector('.kcw__competition-name').textContent,
      handle: input.closest('[data-competition-handle]').dataset.competitionHandle,
      id: input.dataset.competitionId,
      badgePrice: parseFloat(input.dataset.badgePrice || 0),
      badgeVariantId: input.dataset.badgeVariantId || null,
      badgeIsReturnable: input.dataset.badgeIsReturnable === "true",
      laliga: input.dataset.laliga === "true",
      noBadge: input.dataset.noBadge === "true",
      serigraphyHandle: input.dataset.serigraphyHandle || null,
      gender: compGender.toLowerCase()
    };
    try { this.state.serigraphyGeometry = JSON.parse(input.dataset.serigraphy || "{}"); } catch (e) { }

    if (this.state.selectedCompetition.badgeVariantId) {
      this.clearCompetitionError();
    }
  }

  setupQualitySelectors() {
    const inputs = this.section.querySelectorAll('.kcw__selector-radio--quality');
    inputs.forEach(input => {
      input.addEventListener('change', () => {
        this.toggleSelection('.kcw__selector-option--quality', input);
        this.state.selectedQuality = { id: input.dataset.qualityId, handle: input.dataset.qualityHandle, label: input.value };
        if (!this.isUpdatingUI) {
          this.validateVariantAvailability();
          this.refreshProductContext();
          this.preloadGenderImages();
          this.scrollToNextOption('quality');
        }
      });
    });
  }

  setupVariantSelectors() {
    const inputs = this.section.querySelectorAll('.kcw__selector-radio--variant');
    inputs.forEach(input => {
      input.addEventListener('change', () => {
        this.toggleSelection('.kcw__selector-option--variant', input);
        this.state.selectedVariant = {
          ageGroupId: input.dataset.ageGroupMetaId,
          targetGenderId: input.dataset.targetGenderMetaId,
          ageGroupHandle: input.dataset.ageGroupHandle,
          targetGenderHandle: input.dataset.targetGenderHandle,
          label: input.value
        };
        if (!this.isUpdatingUI) {
          this.refreshProductContext();
          this.preloadGenderImages();
          if (this.hasActiveCustomization()) {
            this.drawCustomizations();
          }
          this.scrollToNextOption('variant');
        }
      });
    });
  }

  toggleSelection(selector, input) {
    const labels = this.section.querySelectorAll(selector);
    labels.forEach(l => l.classList.remove('selected'));
    input.closest(selector).classList.add('selected');
  }

  renderOptions(data, containerSel, type) {
    const container = this.section.querySelector(containerSel);
    if (!container) return;
    container.innerHTML = "";
    data.forEach((item, i) => {
      let dataset = "";
      if (type === 'quality') dataset = `data-quality-id="${item.system.id}" data-quality-handle="${item.system.handle}"`;
      if (type === 'variant') dataset = `data-age-group-meta-id="${item.age_group.value.system.id}" data-age-group-handle="${item.age_group.value.system.handle}" data-target-gender-meta-id="${item.target_gender.value.system.id}" data-target-gender-handle="${item.target_gender.value.system.handle}"`;
      const html = `
<label class="kcw__selector-option kcw__selector-option--${type} ${i === 0 ? 'selected' : ''}">
<input type="radio" name="kcw-${type}" value="${item.label}" class="kcw__selector-radio kcw__selector-radio--${type}" ${i === 0 ? 'checked' : ''} ${dataset}>
<span class="kcw__selector-button kcw__selector-button--${type}">${item.label}</span>
</label>`;
      container.insertAdjacentHTML("beforeend", html);
    });

    // Listeners must be re-bound after dynamic DOM insertion
    if (type === 'quality') this.setupQualitySelectors();
    if (type === 'variant') this.setupVariantSelectors();
  }

  updateAllPrices() {
    this.updateKitPrice();
    this.updateCompetitionPrice();
    this.updateCustomizationPrice();
    this.updateSubtotalPrice();
  }

  updateKitPrice() {
    if (this.state.baseProduct) {
      const kitPrice = parseFloat(this.state.baseProduct.price / 100);
      if (this.elKitPrice) {
        this.elKitPrice.textContent = `${this.formatMoney(kitPrice)}`;
      }
    } else {
      if (this.elKitPrice) this.elKitPrice.textContent = '---';
    }
  }

  updateCompetitionPrice() {
    if (this.state.selectedCompetition?.badgePrice) {
      const compPrice = this.state.selectedCompetition.badgePrice / 100;
      if (this.elCompPrice) {
        this.elCompPrice.textContent = `+${this.formatMoney(compPrice)}`;
      }
    } else {
      if (this.elCompPrice) this.elCompPrice.textContent = '';
    }
  }

  updateCustomizationPrice() {
    if (this.elCustomPrice) {
      this.elCustomPrice.textContent = `+${this.formatMoney(this.customizationPrice)}`;
    }
  }

  updateSubtotalPrice() {
    if (this.state.fictitiousProduct?.priceRange?.minVariantPrice?.amount) {
      const totalPrice = parseFloat(this.state.fictitiousProduct.priceRange.minVariantPrice.amount);
      if (this.elSubtotal) {
        this.elSubtotal.textContent = this.formatMoney(totalPrice);
      }
    } else {
      if (this.elSubtotal) this.elSubtotal.textContent = '---';
    }
  }

  handleMobileLayout() {
    const isMobile = window.innerWidth <= 749;
    const bodyC = this.section.querySelector('.kcw__body');
    const optsC = this.section.querySelector('.kcw__options');
    const imgC = this.section.querySelector('.kcw__image-container');
    const totalsC = this.section.querySelector('.kcw__totals');
    const btnC = this.section.querySelector('.kcw__add-to-cart-button');

    if (isMobile) {
      if (imgC && imgC.parentNode !== bodyC) {
        bodyC.insertBefore(imgC, optsC);
      }

      let footerC = bodyC.querySelector('.kcw__footer');
      if (!footerC) {
        footerC = document.createElement('div');
        footerC.className = 'kcw__footer';
      }

      if (totalsC && totalsC.parentNode !== footerC) {
        footerC.appendChild(totalsC);
      }
      if (btnC && btnC.parentNode !== footerC) {
        footerC.appendChild(btnC);
      }

      if (footerC.parentNode !== bodyC) {
        bodyC.appendChild(footerC);
      }

      if (!this.section.style.height) {
        const sectionTop = this.section.getBoundingClientRect().top;
        const height = (sectionTop > 0 && sectionTop < window.innerHeight)
          ? window.innerHeight - sectionTop
          : window.innerHeight;
        this.section.style.height = `${height}px`;
      }
    } else {
      const footerC = bodyC.querySelector('.kcw__footer');

      if (imgC && imgC.parentNode !== bodyC) {
        bodyC.insertBefore(imgC, optsC);
      }

      if (totalsC && totalsC.parentNode !== optsC) {
        optsC.appendChild(totalsC);
      }
      if (btnC && btnC.parentNode !== optsC) {
        optsC.appendChild(btnC);
      }

      if (footerC) {
        footerC.remove();
      }

      this.section.style.height = '';
    }

    this.resizeCanvas();
  }

  scrollToNextOption(currentStep) {
    if (this.isInitializing || window.innerWidth > 749) return;

    const stepOrder = {
      'kit': '.kcw__quality-container',
      'quality': '.kcw__variant-container',
      'variant': '.kcw__size-container',
      'customization': '.kcw__competition-container'
    };

    const nextSelector = stepOrder[currentStep];
    if (!nextSelector) return;

    const nextElement = this.section.querySelector(nextSelector);
    const optionsContainer = this.section.querySelector('.kcw__options');

    if (!nextElement || !optionsContainer) return;

    setTimeout(() => {
      const containerRect = optionsContainer.getBoundingClientRect();
      const elementRect = nextElement.getBoundingClientRect();
      const scrollOffset = elementRect.top - containerRect.top + optionsContainer.scrollTop;

      optionsContainer.scrollTo({
        top: scrollOffset,
        behavior: 'smooth'
      });
    }, 150);
  }

  restoreSelection(sel, val) {
    const inputs = this.section.querySelectorAll(sel);
    const target = Array.from(inputs).find(i => i.value === val) || inputs[0];
    if (target) { target.checked = true; target.dispatchEvent(new Event('change')); }
  }

  setupCustomizationLogic() {
    const tabs = this.section.querySelectorAll('.kcw__tab');
    const contents = this.section.querySelectorAll('.kcw__tab-content');

    if (tabs.length > 0) {
      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          tabs.forEach(t => t.classList.remove('active'));
          contents.forEach(c => c.classList.remove('active'));
          tab.classList.add('active');
          const tabContent = this.section.querySelector(`#kcw-tab-${tab.dataset.tab}`);
          if (tabContent) tabContent.classList.add('active');
          this.state.customizationMode = tab.dataset.tab;
          this.handleTabSwitch();
        });
      });

      const firstTab = tabs[0];
      if (firstTab) {
        this.state.customizationMode = firstTab.dataset.tab;
      }
    }

    const playerSelect = this.querySelector('#kcw-player-select');
    if (playerSelect) {
      playerSelect.addEventListener('change', () => {
        const hadCustomization = this.hasActiveCustomization();
        const option = playerSelect.options[playerSelect.selectedIndex];
        if (option.value) {
          this.state.playerName = option.dataset.name;
          this.state.playerNumber = option.dataset.number;
          this.state.playerReference = option.value;
          this.drawCustomizations();
          this.updateAllPrices();
          this.fetchFictitiousProduct();
          if (!this.isInitializing) {
            this.scrollToNextOption('customization');
          }
        } else {
          this.resetCustomizationState();
          this.drawCustomizations();
          this.updateAllPrices();
          if (hadCustomization) {
            this.fetchFictitiousProduct();
          }
        }
      });
    }
    const nameInput = this.querySelector('#kcw-player-name');
    const numberInput = this.querySelector('#kcw-player-number');
    if (nameInput) {
      nameInput.addEventListener('input', (e) => {
        const hadCustomization = this.hasActiveCustomization();
        let val = e.target.value.replace(/[^A-Za-zÜüÁáÉéÍíÓóÚúÀàÈèÌìÒòÙùÖöÑñÂâÊêÎîÔôÛûĈĉĜĝĞğĤĥĴĵŜŝČčÄäËëÏïĆćÃãÕõÇçĘę´\^¨,.\s\-]/g, '');
        // Same blacklist behaviour as the Kit PDP: warn the user, then clear the
        // input and reset the name customization.
        if (containsBlacklistedWord(val)) {
          nameInput.setCustomValidity('This name contains prohibited words.');
          nameInput.reportValidity();

          setTimeout(() => {
            nameInput.value = '';
            nameInput.setCustomValidity('');

            this.state.playerName = '';
            this.state.playerReference = null;
            this.drawCustomizations();
            this.updateAllPrices();
            if (hadCustomization !== this.hasActiveCustomization()) {
              this.fetchFictitiousProduct();
            }
          }, 100);
        }
        else {
          nameInput.setCustomValidity('');
          this.state.playerName = val;
          this.state.playerReference = null;
          this.drawCustomizations();
          this.updateAllPrices();
          const hasCustomizationNow = this.hasActiveCustomization();
          if (hadCustomization !== hasCustomizationNow) {
            this.fetchFictitiousProduct();
          }
          if (!this.isInitializing && val.length >= 3) {
            this.scrollToNextOption('customization');
          }
        }
      });
    }
    if (numberInput) {
      numberInput.addEventListener('input', (e) => {
        const hadCustomization = this.hasActiveCustomization();
        let val = e.target.value.replace(/\D/g, '').slice(0, 2);
        e.target.value = val;
        this.state.playerNumber = val;
        this.state.playerReference = null;
        this.drawCustomizations();
        this.updateAllPrices();
        const hasCustomizationNow = this.hasActiveCustomization();
        if (hadCustomization !== hasCustomizationNow) {
          this.fetchFictitiousProduct();
        }
        if (!this.isInitializing && val.length >= 1) {
          this.scrollToNextOption('customization');
        }
      });
    }
  }

  handleTabSwitch() {
    const hadCustomization = this.hasActiveCustomization();
    this.resetCustomizationState();
    if (this.state.customizationMode === 'custom') {
      const n = this.querySelector('#kcw-player-name');
      const nu = this.querySelector('#kcw-player-number');
      if (n) n.value = ''; if (nu) nu.value = '';
    }
    if (this.state.customizationMode === 'player') {
      const s = this.querySelector('#kcw-player-select');
      if (s) s.value = "";
    }
    this.drawCustomizations();
    this.updateAllPrices();
    if (hadCustomization) {
      this.fetchFictitiousProduct();
    }
  }

  resetCustomizationState() {
    this.state.playerName = '';
    this.state.playerNumber = '';
    this.state.playerReference = null;
  }

  formatMoney(amount) {
    const languageCode = this._languageCode;
    const countryCode = this._countryCode;
    const currency = this._currency;
    const currencySymbol = this._currencySymbol;

    const locale = `${languageCode.toLowerCase()}-${countryCode}`;
    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const formattedPrice = formatter.format(amount);

    if (formattedPrice === null || formattedPrice === undefined) {
      return `${currencySymbol}0 ${currency}`;
    }

    return `${currencySymbol}${formattedPrice} ${currency}`;
  }

  hasActiveCustomization() {
    return (this.state.playerName && this.state.playerName.trim() !== "") ||
      (this.state.playerNumber && this.state.playerNumber.trim() !== "");
  }

  preloadAllFonts() {
    const fontSet = new Set(['FCB-Play']);
    const compInputs = this.section.querySelectorAll('.kcw__competition-radio');
    compInputs.forEach(input => {
      try {
        const seriData = JSON.parse(input.dataset.serigraphy || "{}");
        if (seriData.font) fontSet.add(seriData.font);
      } catch (e) { }
    });
    fontSet.forEach(fontFamily => {
      document.fonts.load(`bold 16px "${fontFamily}"`).catch(() => { });
    });
  }

  async shopifyQuery(query, variables = {}) {
    const res = await fetch(`https://${this._shopifyDomain}/api/2025-04/graphql.json`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": this._storefrontAccessToken },
      body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) throw new Error("Network Error");
    return res.json();
  }

  async addToCartSubmission() {

    if (!this.state.selectedSizeId) {
      this.showSizeError();
      return;
    }

    if (!this.state.baseProduct) {
      console.error("[KCW] No base product");
      return;
    }

    // The patch is a separate line item and the cart API accepts the payload
    // without it, so a missing badge variant used to produce a kit with no
    // competition patch and no error at all. Block the submission instead.
    const skipBadge = this.shouldSkipBadge();
    if (!skipBadge && !this.state.selectedCompetition?.badgeVariantId) {
      console.error(
        "[KCW] Add to cart blocked: a competition patch is required but none was resolved",
        {
          targetHandle: this.calculateTargetHandle(),
          selectedCompetition: this.state.selectedCompetition?.handle ?? null,
        },
      );
      this.showCompetitionError();
      return;
    }

    this.clearCompetitionError();

    const btn = this.querySelector('#kcw-add-to-cart-btn');
    if (!btn) return;

    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    try {
      const bundleId = crypto.randomUUID();
      const formData = new FormData();

      const baseVariantId = this.state.selectedSizeId;
      const isReturnable = this.state.selectedSizeSource === 'base'
        ? this.state.baseProductIsReturnable
        : this.state.backupProductIsReturnable;

      formData.append("items[0][id]", baseVariantId);
      formData.append("items[0][quantity]", 1);
      formData.append("items[0][properties][_product_id]", bundleId);
      formData.append("items[0][properties][_bundle_id]", bundleId);
      formData.append("items[0][properties][_is_returnable]", isReturnable);

      const customType = this.calculateTargetHandle();
      if (customType) {
        formData.append("items[0][properties][_custom_type]", customType);
      }

      if (this.state.fictitiousProduct?.handle) {
        formData.append("items[0][properties][_fictitious_url]", `/products/${this.state.fictitiousProduct.handle}`);
      }

      // The printing line item follows the product that goes to the cart (the
      // backup one when the size came from it): its customization_service
      // variant when it has one, the generic Customization product otherwise.
      const addedProduct = this.state.selectedSizeSource === 'backup'
        ? this.state.backupProduct
        : this.state.baseProduct;
      const serviceVariantId = addedProduct?.customization_service_variant_id || null;
      const printingVariantId = serviceVariantId || this.customizationVariantId;
      const printingIsReturnable = serviceVariantId
        ? addedProduct.customization_service_is_returnable === true
        : this.customizationIsReturnable;
      // Same precedence as the PDP: the competition's serigraphy, then the
      // product's default one.
      const serigraphyHandle =
        this.state.selectedCompetition?.serigraphyHandle ||
        addedProduct?.default_serigraphy ||
        null;

      const hasCustomization = this.hasActiveCustomization();
      if (hasCustomization && printingVariantId) {
        formData.append("items[1][id]", printingVariantId);
        formData.append("items[1][quantity]", 1);
        formData.append("items[1][properties][_related_product_id]", bundleId);
        formData.append("items[1][properties][_bundle_id]", bundleId);
        formData.append("items[1][properties][_is_returnable]", printingIsReturnable);
        if (serigraphyHandle) {
          formData.append("items[1][properties][_serigraphy]", serigraphyHandle);
        }

        if (this.state.playerName) {
          formData.append("items[1][properties][name]", this.state.playerName.toUpperCase());
        }
        if (this.state.playerNumber) {
          formData.append("items[1][properties][number]", this.state.playerNumber);
        }
      }

      if (!skipBadge && this.state.selectedCompetition?.badgeVariantId) {
        formData.append("items[2][id]", this.state.selectedCompetition.badgeVariantId);
        formData.append("items[2][quantity]", 1);
        formData.append("items[2][properties][_related_product_id]", bundleId);
        formData.append("items[2][properties][_bundle_id]", bundleId);
        formData.append("items[2][properties][_is_returnable]", this.state.selectedCompetition.badgeIsReturnable);
      }

      const data = await cartAdd(formData, {
        sourceElement: btn,
        imageUrl: this.baseImage?.src,
      });

      document.dispatchEvent(new CustomEvent("addToCartSuccess", { detail: data }));

      if (window.kitDataLayer?.dispatchEvent) {
        window.kitDataLayer.dispatchEvent({
          category: "ecommerce",
          action: "cart_add",
          placement: "kcw",
        });
      }

      btn.textContent = '✓ ADDED';
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = originalText;
      }, 2000);

    } catch (error) {
      console.error("[KCW] ❌ Error:", error);
      btn.textContent = 'ERROR';
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = originalText;
      }, 3000);
    }
  }

  // calculateTargetHandle() returns null when the base product has no kit_type,
  // so guard against it: an unresolved type must not be read as "patch not needed".
  shouldSkipBadge() {
    // A competition can intentionally ship without a patch (the "Sin parche"
    // option, whose metaobject references no product_badge, flagged as no_badge).
    // Skip the patch line item and its required-patch guard in that case.
    if (this.state.selectedCompetition?.noBadge) return true;

    const kitType = this.calculateTargetHandle();
    if (!kitType) return false;

    return kitType.includes("pre-printed") ||
      kitType.includes('no-competition');
  }

  showSizeError() {
    if (!this.sizeContainer) return;

    this.sizeContainer.classList.add('has-error');

    if (window.innerWidth <= 749) {
      const optionsContainer = this.section.querySelector('.kcw__options');
      if (optionsContainer) {
        const containerRect = optionsContainer.getBoundingClientRect();
        const elementRect = this.sizeContainer.getBoundingClientRect();
        const scrollOffset = elementRect.top - containerRect.top + optionsContainer.scrollTop;

        optionsContainer.scrollTo({
          top: scrollOffset - 20,
          behavior: 'smooth'
        });
      }
    } else {
      this.sizeContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  showCompetitionError() {
    const competitionContainer = this.section.querySelector('.kcw__competition-container');
    if (!competitionContainer) return;

    competitionContainer.classList.add('has-error');

    if (window.innerWidth <= 749) {
      const optionsContainer = this.section.querySelector('.kcw__options');
      if (optionsContainer) {
        const containerRect = optionsContainer.getBoundingClientRect();
        const elementRect = competitionContainer.getBoundingClientRect();
        const scrollOffset = elementRect.top - containerRect.top + optionsContainer.scrollTop;

        optionsContainer.scrollTo({
          top: scrollOffset - 20,
          behavior: 'smooth'
        });
      }
    } else {
      competitionContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  clearCompetitionError() {
    this.section
      .querySelector('.kcw__competition-container')
      ?.classList.remove('has-error');
  }

  updateAddToCartButton() {
    const btn = this.querySelector('#kcw-add-to-cart-btn');
    if (!btn) return;

    const canAddToCart =
      this.state.baseProduct !== null &&
      this.state.fictitiousProduct !== null;

    btn.disabled = !canAddToCart;
  }
}

if (!customElements.get('kit-customization-widget')) {
  customElements.define('kit-customization-widget', KitCustomizationWidget);
}
